/**
 * ReviewPageHeader — the cream page header both Needs-review pages wear
 * (BON-381): breadcrumb eyebrow, display title, actions on the right.
 *
 * A port of the design bundle's `BookingChrome` header band only. The rail and
 * section bar it also drew are the app's own `NebariAppShell`, which is already
 * mounted around this page, so recreating them would double the chrome.
 */
import { ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export interface ReviewPageHeaderProps {
  /** e.g. `Customers › Needs review`. */
  crumb: string;
  title: string;
  /** Route the back arrow returns to. Omit to hide the arrow (the queue). */
  backTo?: string;
  actions?: ReactNode;
  compact?: boolean;
}

export function ReviewPageHeader({
  crumb,
  title,
  backTo,
  actions,
  compact = false,
}: ReviewPageHeaderProps) {
  return (
    <header
      style={{
        flex: '0 0 auto',
        display: 'flex',
        alignItems: compact ? 'flex-start' : 'flex-end',
        gap: compact ? 12 : 24,
        padding: compact ? '16px 14px 10px' : '22px 32px 14px',
        flexWrap: 'wrap',
      }}
    >
      {backTo && (
        <Link
          to={backTo}
          aria-label="Back to the review queue"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 30,
            height: 30,
            marginBottom: 2,
            borderRadius: 8,
            color: 'var(--nb-subtle)',
            flex: '0 0 auto',
          }}
        >
          <ArrowLeft size={18} />
        </Link>
      )}
      <div style={{ minWidth: 0 }}>
        <div className="nb-eyebrow" style={{ marginBottom: 4, color: 'var(--nb-muted)' }}>
          {crumb}
        </div>
        <h1
          className="nb-display"
          style={{
            margin: 0,
            fontSize: compact ? 22 : 30,
            color: 'var(--nb-bark-dark)',
            letterSpacing: '0.01em',
            lineHeight: 1.05,
          }}
        >
          {title}
        </h1>
      </div>
      <div style={{ flex: 1 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {actions}
      </div>
    </header>
  );
}
